import { Calendar, Users, DollarSign, AlertTriangle, CheckCircle2, XCircle, Repeat, UserPlus } from 'lucide-react'
import type { AppointmentsListResponse } from '@/lib/api-client'
import { formatCurrency } from '@/lib/utils'

interface AppointmentSummaryCardsProps {
  data: AppointmentsListResponse | undefined
  isLoading: boolean
}

function Card({ icon: Icon, label, value, hint, accent }: { icon: typeof Calendar; label: string; value: React.ReactNode; hint?: string; accent?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wide text-text-muted">{label}</span>
        <Icon className="w-4 h-4 text-text-muted" />
      </div>
      <div className={`text-2xl font-bold ${accent ?? 'text-text-dark'}`}>{value}</div>
      {hint && <div className="text-xs text-text-muted mt-1">{hint}</div>}
    </div>
  )
}

export function AppointmentSummaryCards({ data, isLoading }: AppointmentSummaryCardsProps) {
  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4 h-[88px] animate-pulse" />
        ))}
      </div>
    )
  }

  if (data.view === 'rows') {
    const s = data.summary
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card icon={Calendar} label="Total Appts" value={s.total.toLocaleString()} hint="excludes tasks" />
        <Card icon={Users} label="Patients" value={s.uniquePatients} hint="unique people" />
        <Card icon={CheckCircle2} label="Turn Up" value={s.turnUp} accent="text-success" />
        <Card icon={XCircle} label="Cancelled" value={s.cancelled} accent="text-destructive" />
        <Card icon={DollarSign} label="Revenue" value={formatCurrency(s.totalRevenue)} hint={`${s.newSales} new sales`} accent="text-info" />
        <Card icon={AlertTriangle} label="Real No-shows" value={s.realNoShows} hint="closer ads, no PNO" accent="text-warning" />
      </div>
    )
  }

  // patients view
  const s = data.summary
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      <Card icon={Users} label="Patients" value={s.uniquePatients} hint="unique per day" />
      <Card icon={CheckCircle2} label="Attended" value={s.attended} accent="text-success" />
      <Card icon={Repeat} label="Redemption" value={s.likelyRedemption} hint="existing pkg" />
      <Card icon={UserPlus} label="No-show" value={s.noShow} accent="text-warning" hint="new lead" />
      <Card icon={XCircle} label="Cancelled" value={s.cancelled} accent="text-destructive" />
      <Card icon={DollarSign} label="Revenue" value={formatCurrency(s.totalRevenue)} hint={`${s.closerDriven} closer + ${s.inClinicDriven} in-clinic`} accent="text-info" />
    </div>
  )
}
